import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { log } from '../lib/logger';

export interface ColumnWidths {
  [key: string]: number;
}

export type CampaignViewMode = 'list' | 'grid';

export interface UserPreferences {
  id: string;
  user_id: string;
  display_name: string | null;
  theme: 'light' | 'dark';
  language: 'en' | 'pt';
  table_column_widths: ColumnWidths | null;
  campaign_view_mode: CampaignViewMode;
  created_at: string;
  updated_at: string;
}

interface UseUserPreferencesReturn {
  preferences: UserPreferences | null;
  loading: boolean;
  error: Error | null;
  updateLanguage: (language: 'en' | 'pt') => Promise<void>;
  updateColumnWidths: (widths: ColumnWidths) => Promise<void>;
  updateViewMode: (mode: CampaignViewMode) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useUserPreferences(): UseUserPreferencesReturn {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError) throw authError;
      if (!user) {
        setPreferences(null);
        return;
      }

      const { data: prefsData, error: prefsError } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (prefsError) throw prefsError;

      if (!prefsData) {
        const { data: newPrefs, error: insertError } = await supabase
          .from('user_preferences')
          .insert({
            user_id: user.id,
            display_name: user.user_metadata?.full_name || user.email,
            theme: 'light',
            language: 'en',
            campaign_view_mode: 'list',
          })
          .select()
          .single();

        if (insertError) throw insertError;
        setPreferences(newPrefs);
      } else {
        setPreferences({
          ...prefsData,
          campaign_view_mode: prefsData.campaign_view_mode || 'list',
        });
      }
    } catch (err) {
      log.error('Error loading user preferences:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const updateLanguage = async (language: 'en' | 'pt') => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: updateError } = await supabase
        .from('user_preferences')
        .update({
          language,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      setPreferences((prev) => prev ? { ...prev, language } : null);
    } catch (err) {
      log.error('Error updating language:', err);
      throw err;
    }
  };

  const updateColumnWidths = async (widths: ColumnWidths) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: updateError } = await supabase
        .from('user_preferences')
        .update({
          table_column_widths: widths,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      setPreferences((prev) => prev ? { ...prev, table_column_widths: widths } : null);
    } catch (err) {
      log.error('Error updating column widths:', err);
      throw err;
    }
  };

  const updateViewMode = async (mode: CampaignViewMode) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: updateError } = await supabase
        .from('user_preferences')
        .update({
          campaign_view_mode: mode,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      setPreferences((prev) => prev ? { ...prev, campaign_view_mode: mode } : null);
    } catch (err) {
      log.error('Error updating view mode:', err);
      throw err;
    }
  };

  useEffect(() => {
    loadPreferences();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        loadPreferences();
      } else {
        setPreferences(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const refresh = async () => {
    await loadPreferences();
  };

  return { preferences, loading, error, updateLanguage, updateColumnWidths, updateViewMode, refresh };
}
